import {AggregatedDataPoint, TimeResolution} from '@flowmaps-com/flowmaps-typescriptmodels';
import moment, {Moment} from 'moment';
import {WEII_CLIMATE_NORMALIZATION} from '../../../utils/weii-climate-normalization';

interface ClimateTimeRange {
  start?: string | null;
  end?: string | null;
}

export interface ClimateNormalizationPeriod {
  start: Moment;
  end: Moment;
  degreeDays: number;
  referenceDegreeDays: number;
}

export interface ClimateNormalizationTotals {
  degreeDays: number;
  referenceDegreeDays: number;
}

export function supportsClimateNormalizationResolution(resolution?: TimeResolution): boolean {
  return resolution === TimeResolution.month || resolution === TimeResolution.year;
}

/** Future and unfinished calendar buckets are not missing historical climate data. */
export function completedClimateNormalizationRange(
  timeRange: ClimateTimeRange | undefined,
  resolution: TimeResolution | undefined,
  now: Moment = moment()
): ClimateTimeRange | undefined {
  if (!timeRange?.start || !timeRange.end || !supportsClimateNormalizationResolution(resolution)) return undefined;
  const start = moment(timeRange.start);
  const end = moment.min(moment(timeRange.end), now.clone().startOf(resolution === TimeResolution.year ? 'year' : 'month'));
  return start.isValid() && end.isAfter(start)
    ? {start: timeRange.start, end: end.format('YYYY-MM-DDTHH:mm:ss')}
    : undefined;
}

export function getClimateNormalizationTotals(
  timeRange: ClimateTimeRange | undefined,
  points: AggregatedDataPoint[]
): ClimateNormalizationTotals | undefined {
  const periods = getClimateNormalizationPeriods(timeRange, points);
  if (periods.length === 0) return undefined;
  const degreeDays = periods.reduce((sum, period) => sum + period.degreeDays, 0);
  const referenceDegreeDays = periods.reduce((sum, period) => sum + period.referenceDegreeDays, 0);
  return degreeDays > 0 && referenceDegreeDays > 0 ? {degreeDays, referenceDegreeDays} : undefined;
}

export function climateNormalizationFactor(totals: ClimateNormalizationTotals): number {
  return (1 - WEII_CLIMATE_NORMALIZATION.heatingShare)
    + WEII_CLIMATE_NORMALIZATION.heatingShare * totals.referenceDegreeDays / totals.degreeDays;
}

export function colorWithOpacity(color: unknown, opacity: number): unknown {
  if (typeof color !== 'string') return color;
  const match = color.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*[\d.]+)?\s*\)$/i);
  return match ? `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${opacity})` : color;
}

export function getClimateNormalizationPeriods(
  timeRange: ClimateTimeRange | undefined,
  points: AggregatedDataPoint[]
): ClimateNormalizationPeriod[] {
  if (!timeRange?.start || !timeRange.end) return [];
  const rangeStart = moment(timeRange.start);
  const rangeEnd = moment(timeRange.end);
  if (!rangeStart.isValid() || !rangeEnd.isValid() || !rangeEnd.isAfter(rangeStart)) return [];

  const groupedRanges = new Map<string, {start: Moment; end: Moment; values: number[]}>();
  for (const point of points) {
    const sourceStart = moment(point.timeRange?.start);
    const sourceEnd = moment(point.timeRange?.end);
    if (!sourceStart.isValid() || !sourceEnd.isAfter(sourceStart)
      || sourceEnd.isSameOrBefore(rangeStart) || sourceStart.isSameOrAfter(rangeEnd)) continue;
    const start = moment.max(sourceStart, rangeStart);
    const end = moment.min(sourceEnd, rangeEnd);
    const value = Number(point.value);
    if (!Number.isFinite(value)) continue;
    const key = `${start.valueOf()}-${end.valueOf()}`;
    const group = groupedRanges.get(key) || {start, end, values: []};
    group.values.push(value);
    groupedRanges.set(key, group);
  }

  const periods = [...groupedRanges.values()]
    .sort((left, right) => left.start.valueOf() - right.start.valueOf())
    .map(group => ({
      start: group.start,
      end: group.end,
      degreeDays: group.values.reduce((sum, value) => sum + value, 0) / group.values.length,
      referenceDegreeDays: referenceDegreeDaysForRange(group.start, group.end)
    }));

  let coveredUntil = rangeStart.clone();
  for (const period of periods) {
    if (!period.start.isSame(coveredUntil) || !period.end.isAfter(period.start)) return [];
    coveredUntil = period.end.clone();
  }
  return periods.length > 0 && coveredUntil.isSame(rangeEnd) ? periods : [];
}

export function referenceDegreeDaysForRange(start: Moment, end: Moment): number {
  if (!start.isValid() || !end.isAfter(start)) return 0;
  const monthlyReferences = WEII_CLIMATE_NORMALIZATION.monthlyReferenceHeatingDegreeDays;
  const annualScale = WEII_CLIMATE_NORMALIZATION.referenceHeatingDegreeDays
    / monthlyReferences.reduce((sum, value) => sum + value, 0);
  let total = 0;
  let cursor = start.clone();
  while (cursor.isBefore(end)) {
    const nextDay = moment.min(cursor.clone().startOf('day').add(1, 'day'), end);
    const dayStart = cursor.clone().startOf('day');
    const dayEnd = dayStart.clone().add(1, 'day');
    const fractionOfDay = nextDay.diff(cursor) / dayEnd.diff(dayStart);
    total += monthlyReferences[cursor.month()] * annualScale / cursor.daysInMonth() * fractionOfDay;
    cursor = nextDay;
  }
  return total;
}
